import { valueToCollabJSON } from '@databases/collab/convert/to-collab';
import { entryToDatabase, fieldTypeMap } from '@databases/collab/convert/to-database';
import { type YDatabase } from '@databases/collab/y-database';
import { FieldType, type SelectOptionJSON } from '@databases/model/field';
import { type AllValueJSON } from '@databases/model/value';
import {
  type CreateEntryDatabaseValue,
  type CreateEntryFieldError,
  type CreateEntryResult,
  type EntrySdkContract
} from '@databases/sdk-types';
import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { err, ok, type Result } from 'neverthrow';
import { v4 as uuidv4 } from 'uuid';

import {
  createEntryIfAbsentSchema,
  createEntrySchema
} from '../domain/entries/create-schemas.ts';
import {
  addEntrySchema,
  removeEntrySchema,
  reorderEntriesSchema,
  type AddEntryInput,
  type RemoveEntryInput,
  type ReorderEntriesInput
} from '../domain/entries/schemas.ts';
import { createEntryValueValidator } from '../domain/entries/validators.ts';
import { findOrBuildSelectOption } from '../domain/field/utils/find-or-build-select-option.ts';
import { assertEntryExists } from '../domain/guards/assert-entry-exists.ts';
import { assertEntryNotExists } from '../domain/guards/assert-entry-not-exists.ts';
import { assertEntryValueTypesSupported } from '../domain/guards/assert-entry-value-types-supported.ts';
import { assertIndexInRange } from '../domain/guards/assert-index-in-range.ts';
import { parse, tryMutate } from '../domain/result.ts';
import { addEntry } from '../handlers/entry/add-entry.ts';
import { createEntry } from '../handlers/entry/create-entry.ts';
import { deleteEntry } from '../handlers/entry/delete-entry.ts';
import { reorderEntries } from '../handlers/entry/reorder-entries.ts';
import { type DatabaseEntry, type SdkError } from '../types.ts';

const DERIVED_FIELD_TYPES = new Set<FieldType>([
  FieldType.Backlink,
  FieldType.EntryDetails,
  FieldType.JiraIssueDetails,
  FieldType.PageDetails,
  FieldType.PageLabels,
  FieldType.PageStatus
]);

type CreateEntryFieldChange = {
  fieldId: string;
  value: unknown;
};

type CollabFieldChange = {
  fieldId: string;
  fieldType: FieldType;
  value: AllValueJSON;
};

type SelectOptionAddition = {
  fieldId: string;
  option: SelectOptionJSON;
};

type ResolvedCreateEntryValue = {
  value: DatabaseValue;
  selectOptionsToAdd: SelectOptionAddition[];
};

type ValidatedFieldChange =
  | {
      kind: 'valid';
      fieldChange: CollabFieldChange;
      selectOptionsToAdd: SelectOptionAddition[];
    }
  | {
      kind: 'invalid';
      fieldError: CreateEntryFieldError;
    };

type ValidatedFieldChanges =
  | {
      kind: 'valid';
      fieldChanges: CollabFieldChange[];
      fieldErrors: CreateEntryFieldError[];
      selectOptionsToAdd: SelectOptionAddition[];
    }
  | {
      kind: 'invalid';
      fieldErrors: [CreateEntryFieldError, ...CreateEntryFieldError[]];
    };

/**
 * Public API for reading and mutating database entries (rows).
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (ENTRY_NOT_FOUND, ENTRY_ALREADY_EXISTS, INDEX_OUT_OF_RANGE)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Entries are **not** gated by `isLocked`. Content edits stay allowed when the
 * database structure is locked, but Create Entry skips Select values that
 * would add options.
 */
export class EntrySdk implements EntrySdkContract {
  constructor(private readonly ydb: YDatabase) {}

  get(entryId: string): DatabaseEntry | undefined {
    const entry = this.ydb.entriesById[entryId];

    if (!entry) {
      return undefined;
    }

    return entryToDatabase(entryId, entry.toJSON());
  }

  list(): readonly DatabaseEntry[] {
    return this.ydb.entries.flatMap((entryId) => {
      const entry = this.get(entryId);
      return entry ? [entry] : [];
    });
  }

  add(input: AddEntryInput): Result<void, SdkError> {
    return parse(addEntrySchema, input).andThen(({ entry, index }) =>
      assertEntryNotExists(this.ydb, entry.id)
        .andThen(() => assertEntryValueTypesSupported(entry.values))
        .andThen(() =>
          index != null
            ? assertIndexInRange(index, this.ydb.entries.length)
            : ok(undefined)
        )
        .andThen(() =>
          tryMutate(() => addEntry(this.ydb, { entry: entry as DatabaseEntry, index }))
        )
    );
  }

  create(input?: {
    fieldChanges?: readonly CreateEntryFieldChange[];
  }): Result<CreateEntryResult, SdkError> {
    return parse(createEntrySchema, input).andThen((request) => {
      const validation = this.validateFieldChanges(request?.fieldChanges ?? []);
      if (validation.kind === 'invalid') {
        return ok({ kind: 'not-created' as const, fieldErrors: validation.fieldErrors });
      }

      const entryId = uuidv4();
      return tryMutate(() => {
        createEntry(this.ydb, {
          entryId,
          fieldChanges: validation.fieldChanges,
          selectOptionsToAdd: validation.selectOptionsToAdd
        });
        return {
          kind: 'created' as const,
          entryId,
          fieldErrors: validation.fieldErrors
        };
      });
    });
  }

  createIfAbsent(input: {
    entryId: string;
    fieldChanges?: readonly CreateEntryFieldChange[];
  }): Result<CreateEntryResult, SdkError> {
    return parse(createEntryIfAbsentSchema, input).andThen(
      ({ entryId, fieldChanges }) => {
        if (this.ydb.entriesById[entryId]) {
          const result: CreateEntryResult = {
            kind: 'created',
            entryId,
            fieldErrors: []
          };
          return ok(result);
        }

        const validation = this.validateFieldChanges(fieldChanges ?? []);
        if (validation.kind === 'invalid') {
          const result: CreateEntryResult = {
            kind: 'not-created',
            fieldErrors: validation.fieldErrors
          };
          return ok(result);
        }

        return tryMutate(() => {
          createEntry(this.ydb, {
            entryId,
            fieldChanges: validation.fieldChanges,
            selectOptionsToAdd: validation.selectOptionsToAdd
          });
          return {
            kind: 'created' as const,
            entryId,
            fieldErrors: validation.fieldErrors
          };
        });
      }
    );
  }

  private validateFieldChanges(
    fieldChanges: readonly CreateEntryFieldChange[]
  ): ValidatedFieldChanges {
    const fieldErrors: CreateEntryFieldError[] = [];
    const changes: CollabFieldChange[] = [];
    const selectOptionsToAdd: SelectOptionAddition[] = [];
    const seenFieldIds = new Set<string>();

    for (const change of fieldChanges) {
      if (seenFieldIds.has(change.fieldId)) {
        fieldErrors.push({ fieldId: change.fieldId, reason: 'DUPLICATE_FIELD_ID' });
        continue;
      }
      seenFieldIds.add(change.fieldId);

      const validation = this.validateFieldChange(change);
      if (validation.kind === 'invalid') {
        fieldErrors.push(validation.fieldError);
        continue;
      }

      for (const optionToAdd of validation.selectOptionsToAdd) {
        selectOptionsToAdd.push(optionToAdd);
      }
      changes.push(validation.fieldChange);
    }

    const [firstError, ...remainingErrors] = fieldErrors;
    if (changes.length === 0 && firstError) {
      return { kind: 'invalid', fieldErrors: [firstError, ...remainingErrors] };
    }

    return {
      kind: 'valid',
      fieldChanges: changes,
      fieldErrors,
      selectOptionsToAdd
    };
  }

  private validateFieldChange(change: CreateEntryFieldChange): ValidatedFieldChange {
    const field = this.ydb.fieldsById[change.fieldId];
    if (!field || !this.ydb.fields.includes(change.fieldId)) {
      return {
        kind: 'invalid',
        fieldError: { fieldId: change.fieldId, reason: 'FIELD_NOT_FOUND' }
      };
    }
    if (DERIVED_FIELD_TYPES.has(field.type)) {
      return {
        kind: 'invalid',
        fieldError: { fieldId: change.fieldId, reason: 'FIELD_NOT_WRITABLE' }
      };
    }

    const value = createEntryValueValidator.safeParse(change.value);
    if (!value.success || value.data.type !== fieldTypeMap[field.type]) {
      return {
        kind: 'invalid',
        fieldError: { fieldId: change.fieldId, reason: 'INCOMPATIBLE_VALUE' }
      };
    }

    const resolvedValue = this.resolveSelectValue(change.fieldId, value.data);
    if (!resolvedValue) {
      return {
        kind: 'invalid',
        fieldError: { fieldId: change.fieldId, reason: 'FIELD_NOT_WRITABLE' }
      };
    }

    const collabValue = valueToCollabJSON(resolvedValue.value, {});
    if (!collabValue) {
      return {
        kind: 'invalid',
        fieldError: { fieldId: change.fieldId, reason: 'FIELD_NOT_WRITABLE' }
      };
    }

    return {
      kind: 'valid',
      fieldChange: {
        fieldId: change.fieldId,
        fieldType: field.type,
        value: collabValue
      },
      selectOptionsToAdd: resolvedValue.selectOptionsToAdd
    };
  }

  private resolveSelectValue(
    fieldId: string,
    value: CreateEntryDatabaseValue
  ): ResolvedCreateEntryValue | undefined {
    if (value.type !== DatabaseFieldType.SELECT || Array.isArray(value.value)) {
      return { value: value as DatabaseValue, selectOptionsToAdd: [] };
    }

    const field = this.ydb.fieldsById[fieldId];
    const options = field.selectOptions.toJSON();
    const { optionIds, additions } = this.resolveSelectLabels(
      fieldId,
      value.value,
      options
    );

    if (additions.length > 0 && this.ydb.isLocked) {
      return undefined;
    }

    return {
      value: { type: DatabaseFieldType.SELECT, value: optionIds },
      selectOptionsToAdd: additions
    };
  }

  private resolveSelectLabels(
    fieldId: string,
    value: { labels: string[] },
    options: SelectOptionJSON[]
  ): { optionIds: string[]; additions: SelectOptionAddition[] } {
    const availableOptions = [...options];
    const additions: SelectOptionAddition[] = [];
    const optionIds = value.labels.map((label) => {
      const resolution = findOrBuildSelectOption(availableOptions, label);

      if (resolution.kind === 'new') {
        availableOptions.push(resolution.option);
        additions.push({ fieldId, option: resolution.option });
      }

      return resolution.optionId;
    });

    return { optionIds, additions };
  }

  remove(input: RemoveEntryInput): Result<void, SdkError> {
    return parse(removeEntrySchema, input).andThen(({ entryId }) =>
      assertEntryExists(this.ydb, entryId).andThen(() =>
        tryMutate(() => deleteEntry(this.ydb, entryId))
      )
    );
  }

  reorder(input: ReorderEntriesInput): Result<void, SdkError> {
    return parse(reorderEntriesSchema, input).andThen(({ entryIds, newIndex }) => {
      for (const id of entryIds) {
        const check = assertEntryExists(this.ydb, id);

        if (check.isErr()) {
          return err(check.error);
        }
      }

      return assertIndexInRange(
        newIndex,
        this.ydb.entries.length - entryIds.length
      ).andThen(() => tryMutate(() => reorderEntries(this.ydb, { entryIds, newIndex })));
    });
  }
}
